import {useMemo, useState} from 'react';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {Stack} from '@mui/material';
import {ActionHistory, CareTaskChangesHistory} from '../../../backend/interfaces/care-task';
import {CareTaskFeature} from '../../../backend/enums/care-task-feature';
import {translations} from '../../../i18n/translations';
import {LogsHeader} from '../common/logs-header';
import {LogsLoading} from '../common/logs-loading';
import {LogsEmpty} from '../common/logs-empty';
import {useHistoryLogsUsersCache} from './use-care-task-logs-users-cache';
import {TaskLogPartialUpdateAction} from './task-log-partial-update-action';
import {TaskLogRemoveAction} from './task-log-remove-action';
import {convertLegacyHistoryToNewHistory} from './convert-legacy-history-2-new-history';
import {LogsLoadMoreBtn} from '../common/logs-load-more-btn';
import '../history-logs.scss';

export const TaskLogs = ({
  history,
  legacyHistory,
  feature,
  type = 'default',
}: {
  history?: CareTaskChangesHistory[];
  legacyHistory?: ActionHistory[];
  feature?: CareTaskFeature;
  type?: 'default' | 'compact';
}) => {
  const {t} = useTranslation();
  const [itemsNumber, setItemsNumber] = useState(3);

  // Users info cache
  const {users, loading} = useHistoryLogsUsersCache(history, legacyHistory);

  // Sort history logs
  const sortedHistory: CareTaskChangesHistory[] = useMemo(() => {
    // Initilize
    let resultHistory: CareTaskChangesHistory[] = Array.from(history ?? []);

    // Convert legacy history to new history format if provided
    const oldHistoryConverted = convertLegacyHistoryToNewHistory(legacyHistory);
    if (oldHistoryConverted) {
      resultHistory = oldHistoryConverted.concat(resultHistory);
    }

    // Sort by date descending
    resultHistory.sort((a, b) => {
      return new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime();
    });

    return resultHistory;
  }, [history, legacyHistory]);

  const isCompact = type === 'compact';

  return (
    <LogsHeader isCompact={isCompact}>
      {loading ? (
        <LogsLoading />
      ) : (
        <Stack>
          {sortedHistory.length === 0 ? (
            <LogsEmpty />
          ) : (
            sortedHistory.map((history, index) => {
              // itemsNumber is used to limit the number of items displayed
              if (index >= itemsNumber) return null;

              let userAuthorId = history.userAuthorId;
              let changeType = history.changeType;
              const date = moment(history.registeredAt);
              const dateHours = date.format('LT');
              const dateDay = date.format('DD/MM/YYYY');
              const dateStr = `${dateDay} ${t(translations.schedule_field.readable_periodicity.at_hour)} ${dateHours}`;
              const name =
                userAuthorId && users[userAuthorId]
                  ? users[userAuthorId].name
                  : t(translations.care_and_activities.handle_task_modal.history.someone);
              const picture =
                userAuthorId && users[userAuthorId] ? users[userAuthorId].picture : undefined;

              if (changeType === 'partial-update') {
                return (
                  <TaskLogPartialUpdateAction
                    key={`action-${index}`}
                    changes={history.changes}
                    feature={feature}
                    name={name}
                    picture={picture}
                    date={dateStr}
                    compactDisplay={isCompact}
                  />
                );
              }

              if (changeType === 'remove') {
                return (
                  <TaskLogRemoveAction
                    key={`action-${index}`}
                    name={name}
                    picture={picture}
                    date={dateStr}
                    compactDisplay={isCompact}
                  />
                );
              }

              console.log('TaskLogs > Unsupported history type:', history);
              return null;
            })
          )}
          {sortedHistory.length > itemsNumber && (
            <LogsLoadMoreBtn onClick={() => setItemsNumber(itemsNumber + 5)} />
          )}
        </Stack>
      )}
    </LogsHeader>
  );
};
